import {Suspense, useEffect, useRef} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {diApi} from '@di-shared/api/di';
import {ExpectedError} from '@di-shared/features/common/CustomError';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import useErrorCounter from '@di-shared/features/common/useErrorCounter';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import DiagOdometer from '@di-shared/features/diag/DiagOdometer';
import DiagOdometerSkeleton from '@di-shared/features/diag/DiagOdometerSkeleton';
import environmentStore from '@di-shared/features/environment/environmentStore';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import VehicleSestKr from '@di-shared/features/home/kr/vehicle/VehicleSest.kr';
import useCldrDatetimeFormatter from '@di-shared/utils/useCldrDatetimeFormatter';
import {parseDateWithFormat} from '@shared/dateUtils';
import {WebviewUtils} from '@shared/webviewUtils';
import {getQueryKey} from '@shared/queryKeyMap';
import {useSuspenseQuery} from '@tanstack/react-query';

export interface DiagContentsProps {
  vin: string;
  ccid: string;
}

const DIAG_ODOMETER_INDEX = 0;
const KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY = 'kr-sest-auto-sync-suspension-popup-hidden-until';
const ONE_WEEK_IN_MS = 7 * 24 * 60 * 60 * 1000;
const VEHICLE_SEST_INDEX = 1;
const ERROR_COUNT = 2;

function DiagContents({vin, ccid}: DiagContentsProps) {
  const {t} = useInferableTranslation();
  const puid = environmentStore.getState().puid;
  const CldrDatetimeFormatter = useCldrDatetimeFormatter();
  const {resolveFeatureFlag, getExternalVariable} = useFirebaseStore(({resolveFeatureFlag, getExternalVariable}) => ({
    resolveFeatureFlag,
    getExternalVariable,
  }));
  const entryAlertOpenedRef = useRef(false);
  const isSestAutoSyncSuspensionPopupVisible = resolveFeatureFlag('KR_SEST_AUTO_SYNC_SUSPENSION_POPUP', vin);
  const parsedSestAutoSyncSuspensionStartDate = parseDateWithFormat(getExternalVariable('KR_SEST_AUTO_SYNC_SUSPENSION_START_DATE'));
  const sestAutoSyncSuspensionStartDate = parsedSestAutoSyncSuspensionStartDate
    ? CldrDatetimeFormatter.format(parsedSestAutoSyncSuspensionStartDate, 'yMMMEd')
    : CldrDatetimeFormatter.format(new Date('2026-06-24'), 'yMMMEd');
  const sestAutoSyncSuspensionTel = getExternalVariable('KR_SEST_AUTO_SYNC_SUSPENSION_TEL');

  const {data: isCcsFullEnroll} = useSuspenseQuery({
    queryKey: [getQueryKey(diApi.vehicle.getVehicle), vin, puid],
    queryFn: () => diApi.vehicle.getVehicle({vin, puid}),
    select: (data) => data.ccsFullEnrollYn,
  });

  const {isError, emitError} = useErrorCounter(ERROR_COUNT);

  function onDiagOdometerNotRendered() {
    emitError(DIAG_ODOMETER_INDEX);
  }

  function onVehicleSestNotRendered() {
    emitError(VEHICLE_SEST_INDEX);
  }

  useEffect(() => {
    if (isCcsFullEnroll) {
      onDiagOdometerNotRendered();
    }
  }, [isCcsFullEnroll]);

  useEffect(() => {
    if (!isSestAutoSyncSuspensionPopupVisible || entryAlertOpenedRef.current) {
      return;
    }

    entryAlertOpenedRef.current = true;

    const hiddenUntil = Number(window.localStorage.getItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY));

    if (Number.isFinite(hiddenUntil) && hiddenUntil > Date.now()) {
      return;
    }

    if (Number.isFinite(hiddenUntil) && hiddenUntil <= Date.now()) {
      window.localStorage.removeItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY);
    }

    async function openVehicleEntryAlert() {
      const selectedIndex = await WebviewUtils.openCustomAlert({
        title: t('T40005'),
        message: t('T30640_$date_$tel', {date: sestAutoSyncSuspensionStartDate, tel: sestAutoSyncSuspensionTel}),
        buttons: [
          {text: t('T10114'), style: 'PRIMARY', actionType: 'DEFAULT'},
          {text: t('T20178'), style: 'BASIC', actionType: 'CANCEL'},
        ],
      });

      if (selectedIndex === 1) {
        window.localStorage.setItem(KR_SEST_AUTO_SYNC_SUSPENSION_POPUP_HIDDEN_UNTIL_KEY, String(Date.now() + ONE_WEEK_IN_MS));
      }
    }

    openVehicleEntryAlert();
  }, [isSestAutoSyncSuspensionPopupVisible, sestAutoSyncSuspensionStartDate, sestAutoSyncSuspensionTel, t]);

  if (isError) {
    throw new ExpectedError();
  }

  return (
    <div className="wrap">
      <Header right={<HeaderCloseButton />}>
        <h1 className="title_xsmall_b nav_center_title">{t('T10149')}</h1>
      </Header>

      <main className="content page_home">
        {!isCcsFullEnroll && (
          <ErrorBoundary
            fallback={<></>}
            onError={onDiagOdometerNotRendered}
          >
            <Suspense fallback={<DiagOdometerSkeleton />}>
              <DiagOdometer vin={vin} />
            </Suspense>
          </ErrorBoundary>
        )}

        <VehicleSestKr
          vin={vin}
          ccid={ccid}
          onError={onVehicleSestNotRendered}
        />
      </main>
    </div>
  );
}

export default DiagContents;
